import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import KnittingProgramLibraryService from "services/knittingProgramLibrary.service";
import KnittingProgramService from "services/knittingProgram.service";
import { unwrapResponse } from "shared/networking/unwrapResponse";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import {
  knittingProgramKeys,
  knittingProgramLibraryKeys,
  optionsKeys,
} from "shared/queryKeys";
import { IGridifyQuery } from "models/common";
import {
  ICreateKnittingProgramLibraryCommand,
  IKnittingProgramLibraryDeleteReadiness,
  IRevertCommand,
  IStageKnittingProgramResult,
  IUpdateKnittingProgramLibraryCommand,
  IUploadRunResult,
  IUploadSessionConflict,
  KNITTING_PROGRAM_UPLOAD_ERROR,
} from "models/knittingProgramLibrary";
import {
  getFileBaseName,
  getFileExtension,
  isCc4BundlePart,
  isCompleteBundle,
} from "shared/knittingProgramFiles";
import {
  buildCombinedFilter as buildProgramsCombinedFilter,
  buildOrderBy as buildProgramsOrderBy,
} from "../editNew/knittingPrograms.gridify";
import {
  buildCombinedFilter as buildLibrariesCombinedFilter,
  buildOrderBy as buildLibrariesOrderBy,
} from "../overview/knittingProgramLibrary.gridify";
import { toGridifyPaging } from "shared/utils/gridify";
import { executeWithConcurrencyLimit } from "shared/utils/concurrencyUtils";
import { KNITTING_PROGRAM_UPLOAD_CONFIG } from "../knittingProgramUploadConfig";

export function useKnittingProgramLibraries(
  mainProductLine: string,
  searchFilter: string,
  gridFilters: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel,
) {
  return useQuery({
    queryKey: knittingProgramLibraryKeys.list({
      mainProductLine,
      searchFilter,
      gridFilters,
      sortModel,
      paginationModel,
    }),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const query: IGridifyQuery = {
        filter:
          buildLibrariesCombinedFilter(searchFilter, gridFilters) || undefined,
        orderBy: buildLibrariesOrderBy(sortModel),
        ...toGridifyPaging(paginationModel),
      };
      const response = await KnittingProgramLibraryService.getLibraries(
        mainProductLine,
        query,
      );
      return unwrapResponse(response, "Knitting Program Library");
    },
  });
}

export function useKnittingProgramLibrary(id: string) {
  return useQuery({
    queryKey: knittingProgramLibraryKeys.detail(id),
    queryFn: async () => {
      const response = await KnittingProgramLibraryService.getLibrary(id);
      return unwrapResponse(response, "Knitting Program Library");
    },
    enabled: !!id,
  });
}

export function useKnittingPrograms(
  libraryId: string,
  searchFilter: string,
  gridFilters: GridFilterModel,
  sortModel: GridSortModel,
  paginationModel: GridPaginationModel,
) {
  return useQuery({
    queryKey: knittingProgramKeys.list({
      libraryId,
      searchFilter,
      gridFilters,
      sortModel,
      paginationModel,
    }),
    enabled: !!libraryId,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const query: IGridifyQuery = {
        filter:
          buildProgramsCombinedFilter(searchFilter, gridFilters) || undefined,
        orderBy: buildProgramsOrderBy(sortModel),
        ...toGridifyPaging(paginationModel),
      };

      const response = await KnittingProgramService.getPrograms(
        libraryId,
        query,
      );
      return unwrapResponse(response, "Knitting Programs");
    },
  });
}

export function useKnittingProgramLibraryHistory(id: string) {
  return useQuery({
    queryKey: knittingProgramLibraryKeys.history(id),
    queryFn: async () => {
      const response =
        await KnittingProgramLibraryService.getLibraryHistory(id);
      return unwrapResponse(response, "Library History");
    },
    enabled: !!id,
  });
}

export function useCreateKnittingProgramLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (command: ICreateKnittingProgramLibraryCommand) => {
      const response =
        await KnittingProgramLibraryService.createLibrary(command);
      return unwrapResponse(response, "Create Library");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: optionsKeys.circularKnittingProgramLibraries(),
      });
    },
  });
}

export function useUpdateKnittingProgramLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      command,
    }: {
      id: string;
      command: IUpdateKnittingProgramLibraryCommand;
    }) => {
      const response = await KnittingProgramLibraryService.updateLibrary(
        id,
        command,
      );
      return unwrapResponse(response, "Update Library");
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: optionsKeys.circularKnittingProgramLibraries(),
      });
    },
  });
}

export function useDeleteKnittingProgramLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await KnittingProgramLibraryService.deleteLibrary(id);
      return unwrapResponse(response, "Delete Library");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: optionsKeys.circularKnittingProgramLibraries(),
      });
    },
    onSettled: (_data, _error, id) => {
      queryClient.removeQueries({
        queryKey: knittingProgramLibraryKeys.detail(id),
      });
      queryClient.removeQueries({
        queryKey: knittingProgramLibraryKeys.history(id),
      });
    },
  });
}

export function useCheckKnittingProgramLibraryDeleteReadiness() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response =
        await KnittingProgramLibraryService.getDeleteReadiness(id);
      return unwrapResponse<IKnittingProgramLibraryDeleteReadiness>(
        response,
        "Delete Library Readiness",
      );
    },
  });
}

export function useTransferLibraryToDefaultLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response =
        await KnittingProgramLibraryService.transferToDefaultLibrary(id);
      return unwrapResponse(response, "Transfer Library");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: optionsKeys.circularKnittingProgramLibraries(),
      });
    },
  });
}

export function useRevertKnittingProgramLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      command,
    }: {
      id: string;
      command: IRevertCommand;
    }) => {
      const response = await KnittingProgramLibraryService.revertLibrary(
        id,
        command,
      );
      return unwrapResponse(response, "Revert Library");
    },
    onSuccess: async (result, { id }) => {
      // A revert that changed nothing wrote nothing, so there is no stale cache to drop.
      if (!result?.reverted) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.history(id),
      });
      await queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.detail(id),
      });
      await queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
      await queryClient.refetchQueries({
        queryKey: knittingProgramLibraryKeys.history(id),
      });
    },
  });
}

export function useKnittingProgramHistory(programId: string) {
  return useQuery({
    queryKey: knittingProgramKeys.history(programId),
    queryFn: async () => {
      const response =
        await KnittingProgramService.getProgramHistory(programId);
      return unwrapResponse(response, "Program History");
    },
    enabled: !!programId,
  });
}

export function readErrorSubCode(
  errorSubCode: string | undefined,
): number | undefined {
  if (!errorSubCode) return undefined;
  const parsed = Number.parseInt(errorSubCode, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function readConflictParameters(
  messageParameter: unknown,
): IUploadSessionConflict {
  const read = (key: string): string | undefined => {
    if (!messageParameter) return undefined;

    if (Array.isArray(messageParameter)) {
      const match = messageParameter.find(
        (entry) =>
          entry &&
          typeof entry === "object" &&
          (entry as { key?: string }).key === key,
      ) as { value?: string } | undefined;
      return match?.value;
    }

    if (typeof messageParameter === "object") {
      const value = (messageParameter as Record<string, unknown>)[key];
      return typeof value === "string" ? value : undefined;
    }

    return undefined;
  };

  return {
    sessionId: read("sessionId"),
    startedBy: read("startedBy"),
    startedAt: read("startedAt"),
  };
}

export function useUploadKnittingPrograms(
  onFileProgress?: (
    fileIndex: number,
    fileId: string,
    result: IStageKnittingProgramResult,
  ) => void,
  onUploadProgress?: (percent: number) => void,
  onSessionStarted?: (sessionId: string) => void,
) {
  const maxFilesPerChunk = KNITTING_PROGRAM_UPLOAD_CONFIG.MAX_FILES_PER_CHUNK;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      libraryId,
      comment,
      files,
      uploadableFileIds,
      sessionId: existingSessionId,
    }: {
      libraryId: string;
      comment: string;
      files: File[];
      uploadableFileIds?: string[];
      sessionId?: string;
    }): Promise<IUploadRunResult> => {
      const uploadEntries = files.map((file, fileIndex) => {
        const extension = getFileExtension(file.name);
        return {
          file,
          fileId: uploadableFileIds?.[fileIndex] ?? `file-${fileIndex}`,
          originalIndex: fileIndex,
          extension,
          baseName: getFileBaseName(file.name),
          isCc4Part: isCc4BundlePart(extension),
          byteSize: Math.max(file.size, 1),
        };
      });

      const units: (typeof uploadEntries)[] = [];
      const handled = new Set<number>();
      for (const entry of uploadEntries) {
        if (handled.has(entry.originalIndex)) {
          continue;
        }

        if (entry.isCc4Part) {
          const grouped = uploadEntries
            .filter(
              (item) =>
                !handled.has(item.originalIndex) &&
                item.isCc4Part &&
                item.baseName === entry.baseName,
            )
            .sort((a, b) => a.originalIndex - b.originalIndex);
          const hasFullBundle = isCompleteBundle(
            grouped.map((item) => item.extension),
          );
          if (hasFullBundle) {
            grouped.forEach((item) => handled.add(item.originalIndex));
            units.push(grouped);
            continue;
          }
        }

        handled.add(entry.originalIndex);
        units.push([entry]);
      }

      const chunks: (typeof uploadEntries)[] = [];
      let currentChunk: typeof uploadEntries = [];
      let currentChunkSize = 0;
      for (const unit of units) {
        if (
          currentChunkSize > 0 &&
          currentChunkSize + unit.length > maxFilesPerChunk
        ) {
          chunks.push(currentChunk);
          currentChunk = [];
          currentChunkSize = 0;
        }

        currentChunk = [...currentChunk, ...unit];
        currentChunkSize += unit.length;
      }

      if (currentChunk.length > 0) {
        chunks.push(currentChunk);
      }

      let sessionId = existingSessionId;

      if (!sessionId) {
        const sessionResponse = await KnittingProgramService.startUpload(
          libraryId,
          comment,
        );
        const session = sessionResponse.getData();

        if (!sessionResponse.isSuccessfull() || !session?.sessionId) {
          const customError = sessionResponse.getCustomError();
          const isConflict =
            readErrorSubCode(customError?.errorSubCode) ===
            KNITTING_PROGRAM_UPLOAD_ERROR.SESSION_CONFLICT;

          return {
            phase: isConflict ? "startConflict" : "startFailed",
            results: [],
            errorMessage: customError?.message,
            conflict: isConflict
              ? readConflictParameters(customError?.messageParameter)
              : undefined,
          };
        }

        sessionId = session.sessionId;
        onSessionStarted?.(sessionId);
      }

      const totalBytes = uploadEntries.reduce(
        (sum, entry) => sum + entry.byteSize,
        0,
      );
      const stageResults: IStageKnittingProgramResult[] = new Array(
        files.length,
      );

      const chunkInFlightBytes = new Array(chunks.length).fill(0);
      const chunkUploadedBytes = new Array(chunks.length).fill(0);

      await executeWithConcurrencyLimit(
        chunks.map((chunk, chunkIdx) => async () => {
          const chunkBytes = chunk.reduce(
            (sum, entry) => sum + entry.byteSize,
            0,
          );

          try {
            const response = await KnittingProgramService.stageFiles(
              sessionId,
              chunk.map((entry) => entry.file),
              (chunkPercent) => {
                if (!onUploadProgress || totalBytes <= 0) return;
                chunkInFlightBytes[chunkIdx] =
                  chunkBytes * (chunkPercent / 100);
                const totalDone = chunkUploadedBytes.reduce((a, b) => a + b, 0);
                const totalInFlight = chunkInFlightBytes.reduce(
                  (a, b) => a + b,
                  0,
                );
                onUploadProgress(
                  Math.min(
                    ((totalDone + totalInFlight) / totalBytes) * 100,
                    100,
                  ),
                );
              },
            );

            const responseData = response.getData();
            const chunkResults = Array.isArray(responseData)
              ? responseData
              : [];

            chunk.forEach((entry, chunkIndex) => {
              const result: IStageKnittingProgramResult = chunkResults[
                chunkIndex
              ] ?? {
                fileName: entry.file.name,
                programName: entry.baseName,
                programType: "",
                state: "Failed",
                targetVersion: 0,
                unchanged: false,
                errorMessage: "Missing staging result",
              };

              stageResults[entry.originalIndex] = result;
              onFileProgress?.(entry.originalIndex, entry.fileId, result);
            });
          } catch (err) {
            const errorMessage =
              err instanceof Error ? err.message : "Upload failed";
            chunk.forEach((entry) => {
              const result: IStageKnittingProgramResult = {
                fileName: entry.file.name,
                programName: entry.baseName,
                programType: "",
                state: "Failed",
                targetVersion: 0,
                unchanged: false,
                errorMessage,
              };
              stageResults[entry.originalIndex] = result;
              onFileProgress?.(entry.originalIndex, entry.fileId, result);
            });
          } finally {
            chunkUploadedBytes[chunkIdx] = chunkBytes;
            chunkInFlightBytes[chunkIdx] = 0;
            if (onUploadProgress && totalBytes > 0) {
              const totalDone = chunkUploadedBytes.reduce((a, b) => a + b, 0);
              onUploadProgress(Math.min((totalDone / totalBytes) * 100, 100));
            }
          }
        }),
        KNITTING_PROGRAM_UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS,
      );

      const failedCount = stageResults.filter(
        (result) => result?.state === "Failed",
      ).length;

      if (failedCount > 0) {
        return {
          phase: "awaitingConfirmation",
          sessionId,
          results: stageResults,
        };
      }

      const commitResponse = await KnittingProgramService.commitUpload(
        sessionId,
        false,
      );

      if (!commitResponse.isSuccessfull()) {
        const customError = commitResponse.getCustomError();

        return {
          phase: "commitFailed",
          sessionId,
          results: stageResults,
          errorMessage: customError?.message,
          sessionGone: isSessionGone(
            readErrorSubCode(customError?.errorSubCode),
          ),
        };
      }

      return {
        phase: "committed",
        sessionId,
        results: stageResults,
        commit: commitResponse.getData(),
      };
    },
    onSuccess: (_, { libraryId }) => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.detail(libraryId),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
    },
  });
}

export function isSessionGone(errorSubCode: number | undefined): boolean {
  return (
    errorSubCode === KNITTING_PROGRAM_UPLOAD_ERROR.SESSION_NOT_FOUND ||
    errorSubCode === KNITTING_PROGRAM_UPLOAD_ERROR.SESSION_NOT_STAGING
  );
}

export function useCommitKnittingProgramUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
    }: {
      sessionId: string;
      libraryId: string;
    }) => {
      const response = await KnittingProgramService.commitUpload(
        sessionId,
        true,
      );
      return unwrapResponse(response, "Commit Upload");
    },
    onSuccess: (_, { libraryId }) => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.detail(libraryId),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.lists(),
      });
    },
  });
}

export function useAbortKnittingProgramUpload() {
  return useMutation({
    mutationFn: async ({
      sessionId,
      force,
    }: {
      sessionId: string;
      force?: boolean;
    }) => {
      const response = await KnittingProgramService.abortUpload(
        sessionId,
        force ?? false,
      );

      if (response.isSuccessfull()) {
        return;
      }

      if (
        isSessionGone(readErrorSubCode(response.getCustomError()?.errorSubCode))
      ) {
        return;
      }

      throw (
        response.getCustomError() ??
        new Error("Failed to release the upload session.")
      );
    },
  });
}

export function useDeleteKnittingProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const response = await KnittingProgramService.deleteProgram(programId);
      return unwrapResponse(response, "Delete Program");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.all,
      });
    },
  });
}

export function useTransferProgramToDefaultLibrary() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (programId: string) => {
      const response =
        await KnittingProgramService.transferProgramToDefaultLibrary(programId);
      return unwrapResponse(response, "Transfer Program");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: knittingProgramKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.all,
      });
    },
  });
}

export function useRevertKnittingProgram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      programId,
      command,
    }: {
      programId: string;
      command: IRevertCommand;
    }) => {
      const response = await KnittingProgramService.revertProgram(
        programId,
        command,
      );
      return unwrapResponse(response, "Revert Program");
    },
    onSuccess: (result, { programId }) => {
      if (!result?.reverted) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: knittingProgramKeys.history(programId),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: knittingProgramLibraryKeys.all,
      });
    },
  });
}
